<template>
  <div class="auth-options">
    <div class="auth-tabs" role="tablist">
      <button
        type="button"
        class="auth-tab"
        role="tab"
        aria-selected="false"
        @click="switchToSignUp"
      >
        Sign up
      </button>
      <button
        type="button"
        class="auth-tab active"
        role="tab"
        aria-selected="true"
      >
        Log in
      </button>
    </div>

    <h2 class="auth-heading">Welcome back</h2>
    <p class="auth-sub">Pick up where you left off.</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <button
      v-if="onBoardingIncludesGoogle"
      type="button"
      class="btn pro-btn pro-btn--oauth pro-btn--oauth-google w-100 mb-2"
      :disabled="isLoading"
      @click="signInWithGoogle"
    >
      <img
        width="18"
        height="18"
        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
        alt=""
      >
      Continue with Google
    </button>

    <button
      v-if="onBoardingIncludesApple"
      type="button"
      class="btn pro-btn pro-btn--oauth pro-btn--oauth-apple w-100 mb-2"
      :disabled="isLoading"
      @click="signInWithApple"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43Zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282Z"/>
      </svg>
      Continue with Apple
    </button>

    <div v-if="hasSocialOption && hasEmailLikeOption" class="pro-claim-card__divider">OR</div>

    <button
      v-if="onBoardingIncludesEmail"
      type="button"
      class="btn pro-btn pro-btn--secondary w-100 mb-2"
      :disabled="isLoading"
      @click="signInWithEmail"
    >
      <span class="material-icons">mail_outline</span>
      Log in with email
    </button>

    <button
      v-if="onBoardingIncludesMagicLink"
      type="button"
      class="btn pro-btn pro-btn--secondary w-100 mb-2"
      :disabled="isLoading"
      @click="signInWithMagicLink"
    >
      <span class="material-icons">auto_awesome</span>
      Email me a magic link
    </button>

    <small class="legal text-muted">
      By continuing, you accept our
      <a href="/terms-of-service"><u>Terms of Service</u></a>
      and
      <a href="/privacy"><u>Privacy Policy</u></a>.
    </small>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
// eslint-disable-next-line import/extensions,import/no-unresolved
import { OnBoardingPage } from '../../../utils/models/OnBoardingPage';
import { SignInProvider } from '../../../utils/models/SignInProvider';
import { AuthMethods } from '../store/modules/OnBoardingModule';

export default defineComponent({
  name: 'OnBoardingLoginPage',
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    onBoardingIncludesEmail(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.Email);
    },
    onBoardingIncludesGoogle(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.Google);
    },
    onBoardingIncludesApple(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.Apple);
    },
    onBoardingIncludesMagicLink(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.MagicLink);
    },
    hasSocialOption(): boolean {
      return this.onBoardingIncludesGoogle || this.onBoardingIncludesApple;
    },
    hasEmailLikeOption(): boolean {
      return this.onBoardingIncludesEmail || this.onBoardingIncludesMagicLink;
    },
  },
  methods: {
    signInWithGoogle() {
      this.$store.dispatch('onBoarding/signInWith', SignInProvider.Google);
    },
    signInWithApple() {
      this.$store.dispatch('onBoarding/signInWith', SignInProvider.Apple);
    },
    signInWithEmail() {
      // From the login page, "email" goes straight to EmailLogin, not EmailSignUp.
      this.$store.commit('onBoarding/pushPage', OnBoardingPage.EmailLogin);
    },
    signInWithMagicLink() {
      this.$store.dispatch('onBoarding/signInWith', SignInProvider.MagicLink);
    },
    switchToSignUp() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/setPageStack', [OnBoardingPage.SignUpOptions]);
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-options {
  display: flex;
  flex-direction: column;
}

.auth-tabs {
  display: flex;
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 4px;
  margin-bottom: 1.5rem;
  align-self: stretch;
}
.auth-tab {
  flex: 1;
  padding: 0.55rem 1rem;
  border: none;
  background: transparent;
  color: #a1a4ad;
  font-weight: 700;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.auth-tab:hover:not(.active) {
  color: #fff;
}
.auth-tab.active {
  background: linear-gradient(180deg, #ffe93d 0%, var(--yellow, #fddf00) 100%);
  color: var(--dark, #17181D);
  box-shadow: 0 6px 16px -8px rgba(253, 223, 0, 0.45);
}

.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: #a1a4ad;
  font-size: 0.95rem;
  margin: 0 0 1.5rem;
}

.legal {
  display: block;
  text-align: center;
  font-size: 0.72rem;
  color: rgba(161, 164, 173, 0.85);
  margin-top: 0.5rem;
}
.legal a {
  color: rgba(255, 255, 255, 0.75);
}
</style>
